import type React from 'react';
import {useEffect} from 'react';

import {ANNOTATION_EDITOR_ATTRIBUTE} from '../constants';
import {useAnnotationModeStore} from '../store/AnnotationModeStore';

/** By presence: focus is often on `body`, where a dialog's own Escape handler never fires. */
function isDialogOpen(): boolean {
  return document.querySelector(`[aria-modal="true"], [${ANNOTATION_EDITOR_ATTRIBUTE}]`) !== null;
}

/**
 * Exits annotation mode on Escape, yielding to a menu, an open modal, or the style popover.
 * The coachmark is non-modal, so it answers Escape only while it has focus; anywhere else
 * the mode goes off and takes the coachmark with it.
 */
export function useAnnotationModeEscape(toggleRef: React.RefObject<HTMLButtonElement | null>) {
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);

  useEffect(() => {
    if (!isAnnotationModeOn) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || isDialogOpen()) {
        return;
      }
      const state = useAnnotationModeStore.getState();
      if (!state.isAnnotationModeOn) {
        return;
      }
      state.toggleAnnotationMode();
      toggleRef.current?.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAnnotationModeOn, toggleRef]);
}
